(() => {
  'use strict';
  const clean = value => String(value || '').trim().toLowerCase();
  const shapeValues = new Set(['as_shown','round','oval','emerald','pear','cushion','princess','marquise','radiant','asscher','heart']);
  const resolveShape = (current, requested) => {
    const original = shapeValues.has(clean(current)) ? clean(current) : 'as_shown';
    const value = shapeValues.has(clean(requested)) ? clean(requested) : original;
    return {value,current:original,enquiryOnly:value !== original};
  };
  const normalStone = value => /^(cz|cubic[ _-]?zirconia)$/.test(clean(value)) ? 'cubic_zirconia' : 'moissanite';
  const stonePricing = (variant, stone, config) => {
    const enquiryOnly = normalStone(stone) === 'cubic_zirconia';
    if (config.pricingPending) return {enquiryOnly,amountPence:null,formattedPrice:config.labels.pricePending};
    if (!variant) return {enquiryOnly,amountPence:null,formattedPrice:config.labels.unavailable};
    if (!enquiryOnly) return {enquiryOnly:false,amountPence:variant.price,formattedPrice:variant.formattedPrice};
    const amountPence = Number(variant.price) - Number(config.czGuideDiscountPence || 1000);
    if (!Number.isFinite(amountPence) || amountPence <= 0) return {enquiryOnly,amountPence:null,formattedPrice:config.labels.pricePending};
    const money = new Intl.NumberFormat(config.locale || 'en-GB',{style:'currency',currency:config.currency || 'GBP'}).format(amountPence / 100);
    return {enquiryOnly,amountPence,formattedPrice:config.labels.czGuidePrice.replace('__PRICE__',money)};
  };
  const normalSize = value => clean(value).replace(/\s+/g,'').replace(/carats?|cts?/g,'ct');
  const displayAlt = value => String(value || '').split('|').map(part => part.trim()).filter(part => !/^drive\s*:/i.test(part)).map(part => part.replace(/^(finish|size)\s*:\s*/i,'')).filter(Boolean).join(' — ');
  const mediaTags = alt => {
    const text = String(alt || '');
    return {finish: clean(text.match(/(?:^|\|)\s*finish\s*:\s*([^|]+)/i)?.[1] || text.match(/(white|yellow|rose|pink)[-_ ]*view\d/i)?.[1] || text.match(/\b(white|yellow|rose)\s+finish\b/i)?.[1] || ({w:'white',r:'rose',y:'yellow'}[clean(text.match(/@([wry])[-_]/i)?.[1])]) || text.match(/\b(white|yellow|rose)-tone\b/i)?.[1] || (/silver-tone|white-gold tone/i.test(text) ? 'white' : /rose-gold tone/i.test(text) ? 'rose' : /yellow-gold tone/i.test(text) ? 'yellow' : '')),size: normalSize(text.match(/(?:^|\|)\s*size\s*:\s*([^|]+)/i)?.[1])};
  };
  const selectionFinish = (options, values) => {
    const plating = clean(values[options.findIndex(name => /plating|finish/i.test(name))]);
    const metal = clean(values[options.findIndex(name => /base|metal/i.test(name))]);
    if (/rose|pink/.test(plating)) return 'rose';
    if (/rhodium|white/.test(plating)) return 'white';
    if (/yellow/.test(plating)) return 'yellow';
    return /gold/.test(metal) && !/silver/.test(metal) ? 'yellow' : 'white';
  };
  const selectionSize = (options, values) => normalSize(values[options.findIndex(name => /design size|carat|stone size|size|weight/i.test(name))]);
  const matchesMedia = (alt, finish, size) => {
    const tags = mediaTags(alt);
    const finishTag = /^(silver|rhodium|w)$/.test(tags.finish) ? 'white' : /^(pink|r)$/.test(tags.finish) ? 'rose' : /^(gold|y)$/.test(tags.finish) ? 'yellow' : tags.finish;
    return (!finishTag || finishTag === 'all' || finishTag === finish) && (!tags.size || tags.size === 'all' || tags.size === size);
  };
  const gallerySelection = (items,{filter = false,finish = '',size = '',showAll = false,initialId = '',frontId = ''} = {}) => {
    const matching = filter ? items.filter(item => matchesMedia(item.alt,finish,size)) : items;
    const exact = matching.filter(item => {
      const tags = mediaTags(item.alt);
      return tags.finish && tags.finish !== 'all' && (!size || tags.size === size);
    });
    const selected = filter ? matching : items;
    const visible = showAll ? items : selected;
    const preferred = showAll ? visible : exact.length ? exact : matching;
    const verifiedFront = filter && !showAll && matching.find(item => item.id === frontId && item.type === 'image');
    const first = preferred.find(item => item.type === 'video') || verifiedFront || (!filter && items.find(item => item.id === initialId)) || preferred.find(item => item.type === 'image') || visible.find(item => item.type === 'image') || visible.find(item => item.type === 'video') || visible[0];
    return {visible,first,canFilter:selected.length < items.length};
  };
  const enquiryDescription = (title,selections,reference = '',limit = 160) => {
    const name = String(title || '').split(' — ')[0].trim();
    const detail = selections.filter(Boolean).join(' · ');
    const suffix = detail ? ' — ' + detail : '';
    const nameLimit = Math.max(1,limit-suffix.length);
    const shortName = name.length > nameLimit ? name.slice(0,Math.max(1,nameLimit-1)) + '…' : name;
    const description = (shortName + suffix).slice(0,limit);
    return reference && description.length+reference.length+3 <= limit ? description + ' — ' + reference : description;
  };
  const findVariant = (variants, selected) => variants.find(variant => variant.options.length === selected.length && variant.options.every((value,index) => value === selected[index]));
  const compatibleValues = (variants, selected, index) => new Set(variants.filter(variant => variant.options.every((value,at) => at >= index || value === selected[at])).map(variant => variant.options[index]));
  const mount = scope => {
    scope.querySelectorAll('[data-lh-configurator]').forEach(section => {
      if (section.dataset.configured) return;
      section.dataset.configured = 'true';
      let config;
      try { config = JSON.parse(section.querySelector('[data-config-json]').textContent); } catch { return; }
      const pickers = Array.from(section.querySelectorAll('[data-config-option]'));
      const image = section.querySelector('.product-primary-image');
      const imageButton = section.querySelector('[data-image-zoom]');
      const videoStage = section.querySelector('[data-config-video]');
      const allowedMediaIds = new Set((config.allowedMediaIds || []).filter(Boolean).map(String));
      const verifiedVideoFinishes = config.verifiedVideoFinishes || {};
      const mediaAlt = thumb => {
        const finish = verifiedVideoFinishes[String(thumb.dataset.configThumb)];
        return thumb.dataset.configType === 'video' && ['white','yellow','rose'].includes(finish)
          ? `Finish: ${finish} | ${thumb.dataset.configAlt || ''}`
          : thumb.dataset.configAlt;
      };
      const thumbnails = Array.from(section.querySelectorAll('[data-config-thumb]')).filter(thumb => {
        if (!allowedMediaIds.size || allowedMediaIds.has(String(thumb.dataset.configThumb))) return true;
        thumb.remove();
        return false;
      });
      const note = section.querySelector('[data-config-media-note]');
      const allButton = section.querySelector('[data-config-show-all]');
      const status = section.querySelector('[data-config-status]');
      const price = section.querySelector('[data-product-price]');
      const stoneNote = section.querySelector('[data-config-stone-note]');
      const enquiryLink = section.querySelector('.lh-product-actions a[href*="?design="]');
      const enquiryOriginalClass = enquiryLink?.className || '';
      const enquiryOriginalContent = Array.from(enquiryLink?.childNodes || []).map(node => node.cloneNode(true));
      const stoneInputs = config.stonesEnabled ? Array.from(section.querySelectorAll('[data-config-stone-input]')) : [];
      const preferenceInputs = Array.from(section.querySelectorAll('[data-preference-input]'));
      const selectedPreferences = () => preferenceInputs.filter(input => input.checked);
      const selectedStone = () => stoneInputs.find(input => input.checked);
      const shapeFieldset = section.querySelector('[data-config-shape]');
      const shapeInputs = Array.from(section.querySelectorAll('[data-config-shape-input]'));
      const selectedShape = () => shapeInputs.find(input => input.checked);
      const shapeNote = section.querySelector('[data-config-shape-note]');
      const shapePreviewNote = section.querySelector('[data-config-shape-preview]');
      const productForm = section.querySelector('form.product-form');
      const productFormOriginallyHidden = productForm?.hidden || false;
      let shapeProperty = null;
      if (shapeFieldset && productForm) {
        shapeProperty = document.createElement('input');
        shapeProperty.type = 'hidden';
        shapeProperty.name = 'properties[Stone shape]';
        productForm.append(shapeProperty);
      }
      const initialStone = normalStone(new URL(location.href).searchParams.get('stone'));
      stoneInputs.forEach(input => { input.checked = input.value === initialStone; });
      let showAll = false;
      let currentMedia = null;
      const selected = () => pickers.map(picker => picker.querySelector('input:checked')?.value || '');
      const showMedia = thumb => {
        if (!thumb) return;
        const isVideo = thumb.dataset.configType === 'video';
        const template = isVideo ? section.querySelector(`template[data-config-video-id="${thumb.dataset.configThumb}"]`) : null;
        if (isVideo && (!template || !videoStage)) return;
        if (currentMedia !== thumb || !videoStage?.firstElementChild) {
          videoStage?.querySelectorAll('video').forEach(video => video.pause());
          if (videoStage) { videoStage.replaceChildren(); videoStage.hidden = true; }
          if (imageButton) { imageButton.hidden = isVideo; imageButton.disabled = isVideo; }
          if (isVideo) {
            videoStage.append(template.content.cloneNode(true));
            videoStage.hidden = false;
            const video = videoStage.querySelector('video');
            video?.addEventListener('error',() => {
              const fallback = thumbnails.find(item => !item.hidden && item.dataset.configType === 'image') || thumbnails.find(item => item.dataset.configType === 'image');
              if (currentMedia === thumb && fallback) showMedia(fallback);
            },{once:true});
            document.dispatchEvent(new CustomEvent('lh:video-ready',{detail:{video}}));
          } else if (image && thumb.dataset.configImage) {
            image.src = thumb.dataset.configImage;
            image.removeAttribute('srcset'); image.removeAttribute('sizes');
            image.alt = config.labels.imageAlt || config.title;
          }
        }
        currentMedia = thumb;
        thumbnails.forEach(item => { const active = item === thumb; item.classList.toggle('is-active',active); item.setAttribute('aria-pressed',String(active)); });
        const values = selected();
        if (note) { note.hidden = true; note.textContent = ''; }
      };
      const filterMedia = values => {
        const finish = selectionFinish(config.options,values);
        const size = selectionSize(config.options,values);
        const initialId = section.querySelector('[data-config-initial-media]')?.dataset.configInitialMedia;
        const items = thumbnails.map(thumb => ({id:thumb.dataset.configThumb,type:thumb.dataset.configType,alt:mediaAlt(thumb),thumb}));
        const gallery = gallerySelection(items,{filter:pickers.length > 0,finish,size,showAll,initialId,frontId:config.frontMediaByFinish?.[finish]});
        const visible = new Set(gallery.visible.map(item => item.thumb));
        thumbnails.forEach(thumb => { thumb.hidden = !visible.has(thumb); });
        if (allButton) { allButton.hidden = !gallery.canFilter; allButton.textContent = showAll ? config.labels.showSelected : config.labels.showAll; }
        if (gallery.first) {
          showMedia(gallery.first.thumb);
        } else {
          videoStage?.querySelectorAll('video').forEach(video => video.pause());
          if (videoStage) { videoStage.replaceChildren(); videoStage.hidden = true; }
          if (imageButton) { imageButton.hidden = true; imageButton.disabled = true; }
          currentMedia = null;
          if (note) { note.hidden = false; note.textContent = config.labels.mediaMissing; }
        }
      };
      const update = () => {
        pickers.forEach((picker,index) => {
          const values = selected();
          const allowed = compatibleValues(config.variants,values,index);
          const inputs = Array.from(picker.querySelectorAll('input'));
          inputs.forEach(input => { input.disabled = !allowed.has(input.value); });
          if (!inputs.some(input => input.checked && !input.disabled)) {
            const first = inputs.find(input => !input.disabled); if (first) first.checked = true;
          }
        });
        const values = selected();
        const preferences = selectedPreferences();
        const variant = pickers.length ? findVariant(config.variants,values) : config.variants[0];
        const stone = selectedStone();
        const pricing = stonePricing(variant,stone?.value,config);
        const shape = shapeFieldset ? resolveShape(shapeFieldset.dataset.currentShape,selectedShape()?.value) : null;
        const shapeEnquiryOnly = Boolean(shape?.enquiryOnly);
        const enquiryOnly = pricing.enquiryOnly || shapeEnquiryOnly;
        const formattedPrice = shapeEnquiryOnly ? config.labels.pricePending : pricing.formattedPrice;
        const preferenceLabels = preferences.map(input => input.dataset.preferenceLabel || input.value);
        const baseLabels = config.preferenceOnly ? preferenceLabels : values;
        const stoneLabels = stone ? baseLabels.concat(stone.dataset.stoneLabel || stone.value) : baseLabels;
        const shapeLabel = selectedShape()?.dataset.shapeLabel || '';
        const selectionLabels = shapeLabel ? stoneLabels.concat(shapeLabel) : stoneLabels;
        if (price) price.textContent = formattedPrice;
        if (stoneNote) { stoneNote.hidden = !pricing.enquiryOnly; stoneNote.textContent = pricing.enquiryOnly ? config.labels.czEnquiryNote : ''; }
        if (shapeNote) shapeNote.textContent = shapeEnquiryOnly ? shapeFieldset.dataset.shapeEnquiryNote : shapeFieldset.dataset.shapeDefaultNote;
        if (shapePreviewNote) { shapePreviewNote.hidden = !shapeEnquiryOnly; shapePreviewNote.textContent = shapeFieldset.dataset.shapePreviewNote; }
        if (shapeProperty) { shapeProperty.value = shapeLabel; shapeProperty.disabled = enquiryOnly; }
        if (status) status.textContent = config.preferenceOnly ? `${selectionLabels.join(' · ')} — ${config.labels.preferences}` : variant ? `${selectionLabels.join(' · ')} — ${formattedPrice}` : config.labels.unavailable;
        section.querySelectorAll('[data-stone-property]').forEach(input => { input.value = stone?.dataset.stoneLabel || ''; input.disabled = !stone || enquiryOnly; });
        section.querySelectorAll('input[name=id]').forEach(input => { input.value = variant?.id || ''; input.disabled = enquiryOnly; });
        if (productForm) productForm.hidden = productFormOriginallyHidden || shapeEnquiryOnly;
        if (enquiryLink && shapeFieldset) {
          if (shapeEnquiryOnly) {
            enquiryLink.classList.remove('text-link');
            enquiryLink.classList.add('button','button--wide','lh-enquiry-cta');
            enquiryLink.textContent = shapeFieldset.dataset.shapeEnquiryLabel;
          } else {
            enquiryLink.className = enquiryOriginalClass;
            enquiryLink.replaceChildren(...enquiryOriginalContent.map(node => node.cloneNode(true)));
          }
        }
        const addButton = section.querySelector('[data-add-button]');
        if (addButton) addButton.hidden = shapeEnquiryOnly;
        if (addButton) { addButton.disabled = !config.preorderEligible || enquiryOnly || !variant?.available; addButton.textContent = shapeEnquiryOnly ? shapeFieldset.dataset.shapeEnquiryLabel : pricing.enquiryOnly ? config.labels.czEnquiryOnly : config.preorderEligible && variant?.available ? addButton.dataset.availableLabel : addButton.dataset.unavailableLabel; }
        if (variant) {
          const url = new URL(location.href); url.searchParams.set('variant',variant.id);
          if (stone) url.searchParams.set('stone',stone.value); else url.searchParams.delete('stone');
          if (shape) url.searchParams.set('shape',shape.value);
          preferences.forEach(input => url.searchParams.set(input.closest('[data-config-preference]').dataset.configPreference,input.value));
          history.replaceState({},'',url);
          if (enquiryLink) {
            const reference = new URL(location.pathname,location.origin);
            reference.searchParams.set('variant',variant.id);
            if (stone) reference.searchParams.set('stone',stone.value);
            if (shape) reference.searchParams.set('shape',shape.value);
            preferences.forEach(input => reference.searchParams.set(input.closest('[data-config-preference]').dataset.configPreference,input.value));
            const enquiryUrl = new URL(enquiryLink.href,location.origin);
            const enquirySelections = enquiryOnly ? selectionLabels.concat(formattedPrice,shapeEnquiryOnly ? shapeFieldset.dataset.shapeEnquiryLabel : config.labels.czEnquiryOnly) : selectionLabels;
            enquiryUrl.searchParams.set('design',enquiryDescription(config.title,enquirySelections,reference.toString()));
            enquiryUrl.searchParams.set('design_url',reference.toString());
            if (stone) enquiryUrl.searchParams.set('stone',stone.value); else enquiryUrl.searchParams.delete('stone');
            if (shape) enquiryUrl.searchParams.set('shape',shape.value);
            if (pricing.enquiryOnly && !shapeEnquiryOnly && pricing.amountPence != null) enquiryUrl.searchParams.set('guide_price',(pricing.amountPence / 100).toFixed(2)); else enquiryUrl.searchParams.delete('guide_price');
            enquiryLink.href = enquiryUrl.toString();
          }
        }
        showAll = false; filterMedia(values);
        if (config.preferenceOnly) {
          const plating = preferences.find(input => input.closest('[data-config-preference]').dataset.configPreference === 'plating')?.value;
          const base = preferences.find(input => input.closest('[data-config-preference]').dataset.configPreference === 'base')?.value;
          const finish = plating === 'rose' ? 'rose' : plating === 'yellow' || plating === 'unplated' && base !== 'silver' ? 'yellow' : 'white';
          const candidates = thumbnails.filter(thumb => thumb.dataset.configType === 'image' && mediaTags(mediaAlt(thumb)).finish === finish);
          const approvedId = config.frontMediaByFinish?.[finish];
          const matching = thumbnails.find(thumb => thumb.dataset.configThumb === approvedId) || candidates.find(thumb => config.frontMediaIds?.includes(thumb.dataset.configThumb) || /crown[ -]facing|stone[ -]facing/i.test(thumb.dataset.configAlt));
          const matchingVideo = thumbnails.find(thumb => thumb.dataset.configType === 'video' && mediaTags(mediaAlt(thumb)).finish === finish);
          if (matchingVideo) { matchingVideo.hidden = false; showMedia(matchingVideo); }
          else if (matching) { matching.hidden = false; showMedia(matching); }
          else if (config.frontViewPending && candidates.length) {
            candidates[0].hidden = false; showMedia(candidates[0]);
            if (note) { note.hidden = true; note.textContent = ''; }
          }
          else if (note) { note.hidden = true; note.textContent = ''; }
        }
      };
      const initialUrl = new URL(location.href);
      if (shapeFieldset) {
        const initialShape = resolveShape(shapeFieldset.dataset.currentShape,initialUrl.searchParams.get('shape'));
        shapeInputs.forEach(input => { input.checked = input.value === initialShape.value; });
      }
      preferenceInputs.forEach(input => { const key = input.closest('[data-config-preference]').dataset.configPreference; if (initialUrl.searchParams.has(key)) input.checked = input.value === initialUrl.searchParams.get(key); });
      const initial = config.variants.find(variant => String(variant.id) === String(config.selected)) || config.variants[0];
      if (initial) pickers.forEach((picker,index) => picker.querySelectorAll('input').forEach(input => { input.checked = input.value === initial.options[index]; }));
      section.querySelector('[data-config-options]')?.addEventListener('change',update);
      section.querySelector('form.product-form')?.addEventListener('submit',event => {
        const shapeEnquiryOnly = shapeFieldset && resolveShape(shapeFieldset.dataset.currentShape,selectedShape()?.value).enquiryOnly;
        if (normalStone(selectedStone()?.value) !== 'cubic_zirconia' && !shapeEnquiryOnly) return;
        event.preventDefault();
        if (status) status.textContent = shapeEnquiryOnly ? shapeFieldset.dataset.shapeEnquiryNote : config.labels.czEnquiryNote;
        enquiryLink?.focus();
      });
      thumbnails.forEach(thumb => thumb.addEventListener('click',() => showMedia(thumb)));
      section.querySelector('.product-thumbnails')?.addEventListener('keydown',event => {
        const current = event.target.closest('[data-config-thumb]');
        if (!current || !['ArrowLeft','ArrowRight','Home','End'].includes(event.key)) return;
        const visible = thumbnails.filter(thumb => !thumb.hidden); const at = visible.indexOf(current);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? visible.length-1 : (at+(event.key === 'ArrowRight'?1:-1)+visible.length)%visible.length;
        if (visible[next]) { event.preventDefault(); visible[next].focus(); visible[next].click(); }
      });
      allButton?.addEventListener('click',() => { showAll = !showAll; filterMedia(selected()); });
      if (pickers.length || stoneInputs.length || preferenceInputs.length || shapeInputs.length) { update(); if (shapeFieldset) shapeFieldset.hidden = false; }
      else if (thumbnails.length) filterMedia([]);
      section.closest('.shopify-section')?.addEventListener('shopify:section:unload',() => videoStage?.querySelectorAll('video').forEach(video => video.pause()));
    });
  };
  const api = {resolveShape,normalStone,stonePricing,gallerySelection,enquiryDescription,mediaTags,selectionFinish,selectionSize,matchesMedia,findVariant,compatibleValues,displayAlt};
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof document !== 'undefined') {
    mount(document);
    document.addEventListener('shopify:section:load',event => mount(event.target));
  }
})();





